<template>
  <v-container class="contact-page" fluid>
    <v-popup-loading :isLoading="isPageLoading" />
    <v-row>
      <v-col>
        <v-breadcrumbs :items="breadcrumbs" divider=">" class="px-0 pb-0"></v-breadcrumbs>
      </v-col>
    </v-row>

    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">{{ isEditMode ? 'Edit Facility' : 'Create Facility' }}</h1>
        <p class="subhead mb-0">Fields marked <span class="required">*</span> are required.</p>
      </div>
      <div class="top-actions">
        <v-btn outlined class="mr-2" @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save</v-btn>
      </div>
    </div>

    <v-alert v-if="saveError" type="error" dense outlined class="mb-4">{{ saveError }}</v-alert>

    <v-card class="form-card">
      <div class="form-card-head">Category</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="4">
            <label>CATEGORY <span class="required">*</span></label>
            <v-select
              dense
              outlined
              hide-details
              v-model="form.category"
              :items="categoryOptions"
              placeholder="Select category"
            />
          </v-col>
          <v-col v-if="isSuperAdmin" cols="12" md="4">
            <label>BRANCH <span class="required">*</span></label>
            <v-select dense outlined hide-details :items="orgOptions" item-text="text" item-value="value" v-model="form.org_codename" placeholder="Select Branch"/>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Facility Details</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6"><label>FACILITY NAME <span class="lang">TH</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.name_th" placeholder="เช่น คอฟฟี่ เลานจ์"/></v-col>
          <v-col cols="12" md="6"><label>FACILITY NAME <span class="lang">EN</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.name_en" placeholder="e.g. Coffee Lounge"/></v-col>
          <v-col cols="12" md="6"><label>DESCRIPTION <span class="lang">TH</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.description_th" placeholder="รายละเอียด..."/></v-col>
          <v-col cols="12" md="6"><label>DESCRIPTION <span class="lang">EN</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.description_en" placeholder="Description..."/></v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Location & Hours</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6"><label>BUILDING <span class="lang">อาคาร</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.building" placeholder="e.g. Building A"/></v-col>
          <v-col cols="12" md="6"><label>FLOOR <span class="lang">ชั้น</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.floor" placeholder="e.g. Floor 1"/></v-col>
          <v-col cols="12" md="6">
            <label>OPEN TIME <span class="required">*</span></label>
            <v-select
              dense
              outlined
              hide-details
              :items="timeOptions"
              item-text="label"
              item-value="value"
              v-model="form.open_time"
              placeholder="Select open time"
              @change="onOpenTimeChange"
            />
          </v-col>
          <v-col cols="12" md="6">
            <label>CLOSE TIME <span class="required">*</span></label>
            <v-select
              dense
              outlined
              hide-details
              :items="closeTimeOptions"
              item-text="label"
              item-value="value"
              v-model="form.close_time"
              placeholder="Select close time"
              @change="onCloseTimeChange"
            />
            <div v-if="showTimeValidationError" class="field-error mt-1">Close time must be later than open time.</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Media Uploads</div>
      <v-card-text class="pt-4">
        <label>FACILITY ICON / PHOTO</label>
        <input
          ref="coverInput"
          type="file"
          accept="image/*"
          class="d-none"
          @change="onCoverFileChange"
        />
        <div
          class="upload-box mb-3"
          @click="openCoverPicker"
          @dragover.prevent
          @drop.prevent="onCoverDrop"
        >
          <v-icon class="upload-icon" size="26">mdi-cloud-upload-outline</v-icon>
          Browse Image or drop it here<br/><b>Upload image to OBS</b>, Max file size: 3 MB
        </div>
        <div v-if="coverPreview" class="cover-preview 'mb-4">
          <img :src="coverPreview" alt="cover"/>
          <button type="button" class="thumb-remove" @click="removeCover">×</button>
        </div>
        <div v-if="uploadError" class="upload-error mt-2">{{ uploadError }}</div>
      </v-card-text>
    </v-card>

    <v-card class="form-card actions-card">
      <v-card-actions class="modal-foot">
        <v-spacer />
        <v-btn outlined @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save Facility</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'
import PopupLoading from '@/components/PopupLoading'

const defaultForm = () => ({
  id: null,
  org_codename: '',
  category: '',
  name_th: '',
  name_en: '',
  description_th: '',
  description_en: '',
  building: '',
  floor: '',
  open_time: '',
  close_time: '',
  cover_image: '',
  images: []
})
const MAX_IMAGE_SIZE_BYTES = 3 * 1024 * 1024 //3MB
const FACILITY_UPLOAD_FOLDER = 'patient-services/facilities'

export default {
  components: {
    'v-popup-loading': PopupLoading
  },
  data() {
    return {
      form: defaultForm(),
      coverPreview: '',
      galleryImages: [],
      saveError: '',
      uploadError: '',
      initialLoading: false
    }
  },
  computed: {
    ...mapState('facilities', ['list', 'isSaving', 'orgOptions']),
    ...mapState('auth', ['user']),
    isPageLoading() { return this.initialLoading },
    isEditMode() { return !!this.$route.params.id },
    isSuperAdmin() {
      const currentUser = this.user || {}
      return !!currentUser.is_superuser
    },
    canSave() { return this.isEditMode ? hasPermision('CanEditFacility') : hasPermision('CanAddFacility') },
    isFormValid() {
      return !!(
        this.form.org_codename && this.form.category && this.form.name_th && this.form.name_en &&
        this.form.building && this.form.floor && this.form.open_time && this.form.close_time && this.isCloseTimeValid
      )
    },
    timeOptions() {
      const options = []
      for (let hour = 0; hour < 24; hour += 1) {
        for (let minute = 0; minute < 60; minute += 30) {
          const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
          options.push({value, label: this.to12HourLabel(value)})
        }
      }
      return options
    },
    closeTimeOptions() {
      if (!this.form.open_time) return this.timeOptions
      const openMinutes = this.toMinutes(this.form.open_time)
      return this.timeOptions.filter(item => this.toMinutes(item.value) > openMinutes)
    },
    isCloseTimeValid() {
      if (!this.form.open_time || !this.form.close_time) return true
      return this.toMinutes(this.form.close_time) > this.toMinutes(this.form.open_time)
    },
    showTimeValidationError() {
      return !!(this.form.open_time && this.form.close_time && !this.isCloseTimeValid)
    },
    categoryOptions() {
      const preset = ['Food & Drink', 'Convenience', 'Services & Lounges']
      const dynamic = this.list.map(item => (item.category || '').trim()).filter(Boolean)
      return Array.from(new Set([...preset, ...dynamic]))
    },
    breadcrumbs() {
      return [
        {text: 'Dashboard', exact: true, href: '/'},
        {text: 'Patient Services', disabled: true, href: '/patient-services/facilities'},
        {text: 'Facilities', disabled: true, href: '/patient-services/facilities'},
        // {text: this.isEditMode ? 'Edit' : 'Create', disabled: true, href: '#'}
      ]
    }
  },
  async created() {
    this.initialLoading = true
    try {
      this.setResource('facilities')
      await this.fetchOrgOptions()
      await this.fetch()

      if (this.isEditMode) {
        const target = this.list.find(item => String(item.id) === String(this.$route.params.id))
        if (target) {
          this.form = {...defaultForm(), ...target}
          this.coverPreview = target.cover_image || ''
          this.galleryImages = Array.isArray(target.images) ? target.images.map((url, index) => ({url, name: `photo-${index + 1}`})) : []
        }
      } else {
        const userBranch = this.getUserOrgCode() || this.orgOptions[0]?.value || ''
        this.form.org_codename = userBranch
      }
    } finally {
      this.initialLoading = false
    }
  },
  methods: {
    ...mapActions('facilities', ['setResource', 'fetch', 'save', 'fetchOrgOptions', 'upload']),
    getUserOrgCode() {
      const currentUser = this.user || {}
      return currentUser.org_code || currentUser.org_codename || currentUser.org || ''
    },
    to12HourLabel(timeValue) {
      const [hourRaw, minuteRaw] = String(timeValue || '').split(':').map(Number)
      if (!Number.isFinite(hourRaw) || !Number.isFinite(minuteRaw)) return ''
      const meridiem = hourRaw >= 12 ? 'PM' : 'AM'
      const hour12 = hourRaw % 12 === 0 ? 12 : hourRaw % 12
      return `${String(hour12).padStart(2, '0')}:${String(minuteRaw).padStart(2, '0')} ${meridiem}`
    },
    toMinutes(timeValue) {
      const [hour, minute] = String(timeValue || '').split(':').map(Number)
      if (!Number.isFinite(hour) || !Number.isFinite(minute)) return 0
      return (hour * 60) + minute
    },
    onOpenTimeChange() {
      if (!this.form.open_time || !this.form.close_time) return
      if (!this.isCloseTimeValid) {
        this.form.close_time = ''
      }
    },
    onCloseTimeChange() {
      if (!this.form.open_time || !this.form.close_time) return
      if (!this.isCloseTimeValid) {
        this.form.close_time = ''
      }
    },
    fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    async uploadImage(file, namePrefix) {
      const base64 = await this.fileToDataUrl(file)
      const uploadName = `${namePrefix}_${Date.now()}`
      const imageUrl = await this.upload({
        name: uploadName,
        base64,
        folder: FACILITY_UPLOAD_FOLDER
      })
      if (!imageUrl) {
        this.uploadError = 'Upload failed. Please try again.'
      }
      return imageUrl
    },
    validateImageFile(file) {
      if (!file || !file.type.startsWith('image/')) {
        this.uploadError = 'Please select an image file.'
        return false
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        this.uploadError = 'Max 3 MB per photo.'
        return false
      }
      return true
    },
    openCoverPicker() {
      this.$refs.coverInput && this.$refs.coverInput.click()
    },
    openGalleryPicker() {
      if (this.galleryImages.length >= 5) {
        this.uploadError = 'You can upload up to 5 gallery images.'
        return
      }
      this.$refs.galleryInput && this.$refs.galleryInput.click()
    },
    async onCoverFileChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file || !this.validateImageFile(file)) return

      if (this.coverPreview || this.form.cover_image) {
        const shouldOverride = window.confirm('A cover image already exists. Do you want to replace it?')
        if (!shouldOverride) {
          event.target.value = null
          return
        }
      }

      this.uploadError = ''
      const imageUrl = await this.uploadImage(file, 'facility_cover')
      if (!imageUrl) return
      this.coverPreview = imageUrl
      this.form.cover_image = imageUrl
      event.target.value = null
    },
    async onGalleryFileChange(event) {
      await this.processGalleryFiles(Array.from(event.target.files || []))
      event.target.value = null
    },
    async onCoverDrop(event) {
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      if (!file || !this.validateImageFile(file)) return

      if (this.coverPreview || this.form.cover_image) {
        const shouldOverride = window.confirm('A cover image already exists. Do you want to replace it?')
        if (!shouldOverride) return
      }

      this.uploadError = ''
      const imageUrl = await this.uploadImage(file, 'facility_cover')
      if (!imageUrl) return
      this.coverPreview = imageUrl
      this.form.cover_image = imageUrl
    },
    async onGalleryDrop(event) {
      await this.processGalleryFiles(Array.from(event.dataTransfer.files || []))
    },
    async processGalleryFiles(files) {
      if (!files.length) return
      this.uploadError = ''
      const available = 5 - this.galleryImages.length
      const selected = files.slice(0, available)
      if (files.length > available) {
        this.uploadError = 'Only first 5 images are accepted.'
      }

      for (const file of selected) {
        if (!this.validateImageFile(file)) continue
        const imageUrl = await this.uploadImage(file, 'facility_gallery')
        if (!imageUrl) continue
        this.galleryImages.push({url: imageUrl, name: file.name})
      }
    },
    removeCover() {
      this.coverPreview = ''
      this.form.cover_image = ''
    },
    removeGalleryImage(index) {
      this.galleryImages.splice(index, 1)
    },
    onCancel() {
      this.$router.push({name: 'facility-list'})
    },
    async onSave() {
      const payload = {
        ...this.form,
        cover_image: this.form.cover_image,
        images: this.galleryImages.map(item => item.url)
      }
      const result = await this.$store.dispatch('facilities/save', payload)
      if (!result.ok) {
        this.saveError = result.message
        return
      }
      this.$router.push({name: 'facility-list'})
    }
  }
}
</script>

<style scoped>
.contact-page { background: #f7f9fc; min-height: calc(100vh - 120px); }
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.headline { font-size: 30px !important; color: #0f1f3b; }
.subhead { color: #6d7f9e; font-size: 14px; }
.top-actions { display: flex; align-items: center; }
.form-card { border: 1px solid #e5ebf6; border-radius: 12px; margin-bottom: 14px; background: #fff; overflow: hidden; }
.form-card-head { padding: 12px 14px; border-bottom: 1px solid #edf2fb; font-size: 18px; font-weight: 700; color: #0f1f3b; background: #fff; }
.head-sub { font-size: 12px; color: #7586a8; font-weight: 500; margin-top: 4px; }
.modal-foot { border-top: 1px solid #edf2fb; padding: 10px 14px; }
.upload-box { border: 1.5px dashed #d9e5f8; border-radius: 10px; color: #7a8cad; padding: 22px; text-align: center; font-size: 14px; }
.upload-icon { display: block; margin: 0 auto 8px; color: #9ab0d6; }
.gallery-row { margin-top: 12px; display: flex; gap: 10px; }
.gallery-thumb { width: 96px; height: 68px; border-radius: 8px; border: 1px solid #e5ebf6; background: #f4f7fc; color: #9aaccc; display: flex; align-items: center; justify-content: center; font-size: 12px; position: relative; overflow: hidden; }
.gallery-thumb.add { border-style: dashed; color: #2a62d9; font-size: 24px; background: #fff; }
.gallery-thumb.has-image img { width: 100%; height: 100%; object-fit: cover; }
.cover-preview { width: 160px; height: 100px; border: 1px solid #e5ebf6; border-radius: 8px; overflow: hidden; position: relative; }
.cover-preview.is-broken { border-color: transparent; }
.cover-preview img { width: 100%; height: 100%; object-fit: cover; border: 0; outline: none; }
.thumb-remove { position: absolute; top: 4px; right: 4px; width: 20px; height: 20px; border-radius: 50%; border: 1px solid #f2bcbc; background: #fff; color: #c62828; line-height: 16px; font-size: 14px; cursor: pointer; }
.upload-error { color: #c62828; font-size: 12px; }
.field-error { color: #c62828; font-size: 12px; }
.required { color: #c62828; }
.lang { color: #2962ff; }
.actions-card { margin-bottom: 0; }
label { font-size: 12px; color: #64748b; font-weight: 600; display: block; margin-bottom: 6px; }
</style>
